// Pure-data reference tables for the Plane-faithful Tasks model. NO DOM, NO
// component imports — just our own typed constants the toolbar / board / list /
// peek read from so labels, ordering, and grouping options live in ONE place
// instead of being re-hardcoded per surface.
//
// These mirror Plane's task SEMANTICS (the five state GROUPS, the five PRIORITY
// levels, and the Display-menu option lists) but every value is OUR OWN: our
// wording, our column keys (board.ts), our priority keys (priority.ts), and our
// app.css token names (the --state-* state-group scale + the --priority-* scale).
// Plane's colorimetry is adopted in the TOKEN DEFINITIONS in app.css; this file
// only references the token names. Nothing is copied from any external file.

import type { Priority } from "./priority.js";
import type { DisplayKey, GroupBy, OrderBy } from "./view.js";

// ── STATE GROUPS ─────────────────────────────────────────────────────────────
// Plane organises issue states into five GROUPS: backlog / unstarted / started /
// completed / cancelled. This is now the board's ONLY column vocabulary
// (board.ts's legacy four-column todo/in_progress/pending_review/done set is
// retired) — the board renders one column per group, so the group key IS the
// board column key (board.ts's `ColumnKey` is this same type). `seedColor` is an
// app.css token NAME (resolved at the call site as a theme utility, never a raw
// literal); `iconKey` is a stable lucide-ish glyph key the surface maps to its
// own icon component; `order` is the canonical left-to-right / top-to-bottom
// display order.
export interface StateGroup {
  // Stable key (Plane's state-group vocabulary, our own value).
  key: "backlog" | "unstarted" | "started" | "completed" | "cancelled";
  // Human-facing label (our wording).
  label: string;
  // app.css token name for the group's seed/accent color. Resolved at the call
  // site (e.g. `text-[color:var(${seedColor})]`), never inlined as a literal.
  seedColor: string;
  // Stable glyph key the rendering surface maps to its own icon component.
  iconKey: "circle-dashed" | "circle" | "circle-dot" | "circle-check" | "circle-x";
  // Canonical display order.
  order: number;
}

export const STATE_GROUPS: StateGroup[] = [
  {
    key: "backlog",
    label: "Backlog",
    seedColor: "--state-backlog",
    iconKey: "circle-dashed",
    order: 0,
  },
  {
    key: "unstarted",
    label: "To Do",
    seedColor: "--state-unstarted",
    iconKey: "circle",
    order: 1,
  },
  {
    key: "started",
    label: "In Progress",
    seedColor: "--state-started",
    iconKey: "circle-dot",
    order: 2,
  },
  {
    key: "completed",
    label: "Done",
    seedColor: "--state-completed",
    iconKey: "circle-check",
    order: 3,
  },
  {
    key: "cancelled",
    label: "Cancelled",
    seedColor: "--state-cancelled",
    iconKey: "circle-x",
    order: 4,
  },
];

export type StateGroupKey = StateGroup["key"];

// Resolve a state group's seed color as a ready-to-use CSS color value
// (`var(--state-<group>)`), so a surface that only carries a bare state group
// (e.g. the calendar chip's stripe or the gantt bar's fill) can color itself
// token-only, with ZERO raw hex — exactly how StateGroupIcon tints its glyph.
// An unknown group falls back to the first group's token (backlog).
export function stateGroupColorVar(group: StateGroupKey): string {
  const meta = STATE_GROUPS.find((g) => g.key === group) ?? STATE_GROUPS[0];
  return `var(${meta.seedColor})`;
}

// ── PRIORITIES ───────────────────────────────────────────────────────────────
// The five priority levels (priority.ts Priority), paired with a label, a stable
// icon key, and the app.css priority token NAME. The token-name indirection
// keeps callers free of raw color literals: a priority dot reads
// `bg-priority-<key>`, a detail control reads `text-priority-<key>`, both driven
// off `token` here. Order mirrors PRIORITY_ORDER (urgent → low) then "none".
export interface PriorityMeta {
  key: Priority;
  label: string;
  // Lucide glyph keys, matched 1:1 to Plane's PriorityIcon (lucide-react):
  // urgent=AlertCircle, high=SignalHigh, medium=SignalMedium, low=SignalLow,
  // none=Ban. PriorityIcon maps each key to the corresponding @lucide/svelte icon.
  iconKey: "alert-circle" | "signal-high" | "signal-medium" | "signal-low" | "ban";
  // app.css token name for this priority (the --priority-* scale). Resolved at
  // the call site as a theme utility (bg-priority-<key> / text-priority-<key>).
  token: string;
}

export const PRIORITIES: PriorityMeta[] = [
  { key: "urgent", label: "Urgent", iconKey: "alert-circle", token: "--priority-urgent" },
  { key: "high", label: "High", iconKey: "signal-high", token: "--priority-high" },
  { key: "medium", label: "Medium", iconKey: "signal-medium", token: "--priority-medium" },
  { key: "low", label: "Low", iconKey: "signal-low", token: "--priority-low" },
  { key: "none", label: "No priority", iconKey: "ban", token: "--priority-none" },
];

// ── DISPLAY-MENU OPTION LISTS ────────────────────────────────────────────────
// The three option lists the work-items "Display" popover renders. Each is a
// {value,label} pair list so the menu never hardcodes its own labels. `value`
// references the matching view.ts union (GroupBy / OrderBy / DisplayKey) so the
// option lists can never drift from the functions that consume them — a new
// GroupBy that isn't listed here is a type error.

export interface DisplayOption<T extends string> {
  value: T;
  label: string;
}

// "Group by" options — how the board/list buckets work items. Mirrors the
// GroupBy union (state group / assignee / priority) plus the additive facets
// view.ts now understands (project / label / cycle / module / created by), and
// the "None" no-grouping mode (a single flat list) LAST. The legacy "Status"
// grouping (the four-column todo/in_progress/pending_review/done vocabulary,
// board.ts) is RETIRED — "State group" (the five real Plane phases: Backlog /
// To Do / In Progress / Done / Cancelled) is the sole state-grouping facet now.
export const GROUP_BY_OPTIONS: DisplayOption<GroupBy>[] = [
  { value: "stateGroup", label: "State group" },
  { value: "priority", label: "Priority" },
  { value: "assignee", label: "Assignee" },
  { value: "createdBy", label: "Created by" },
  { value: "project", label: "Department" },
  { value: "label", label: "Label" },
  { value: "cycle", label: "Cycle" },
  { value: "module", label: "Module" },
  { value: "none", label: "None" },
];

// "Order by" options — the sort field the Ordering radio list offers. Mirrors
// the OrderBy union (every value maps to a real Task field; see view.ts).
// "Manual" (sort_order) is Plane's DEFAULT drag-to-reorder ordering, listed first.
export const ORDER_BY_OPTIONS: DisplayOption<OrderBy>[] = [
  { value: "sortOrder", label: "Manual" },
  { value: "createdAt", label: "Created" },
  { value: "updatedAt", label: "Updated" },
  { value: "dueAt", label: "Due date" },
  { value: "priority", label: "Priority" },
  { value: "title", label: "Title" },
];

// "Display properties" toggle list — which property chips a card/row renders.
// Mirrors the DisplayKey union (every key maps to a property the card can
// actually render; see view.ts DISPLAY_OPTIONS / DEFAULT_DISPLAY). The Plane-parity
// additions (labels / start date / cycle / module / created by) each map to a real
// Task field the shared property strip renders; estimate / sub-issue / attachment /
// link are intentionally omitted (no data on our Task).
export const DISPLAY_PROPERTY_OPTIONS: DisplayOption<DisplayKey>[] = [
  { value: "status", label: "Status" },
  { value: "priority", label: "Priority" },
  { value: "assignee", label: "Assignee" },
  { value: "dueAt", label: "Due date" },
  { value: "startDate", label: "Start date" },
  { value: "labels", label: "Labels" },
  { value: "cycle", label: "Cycle" },
  { value: "module", label: "Module" },
  { value: "taskId", label: "ID" },
];

// ── LABEL PALETTE ────────────────────────────────────────────────────────────
// The 8 functional label colors (matching the app.css --label-* palette). A
// label chip resolves its triad off one of these names via the Tailwind bridge:
// bg-label-<name>-bg text-label-<name>-text border-label-<name>-border. Exposed
// as data so a label picker / deterministic name→color hash reads one ordered
// list instead of re-listing the names per surface.
export const LABEL_COLORS = [
  "indigo",
  "emerald",
  "grey",
  "crimson",
  "yellow",
  "orange",
  "pink",
  "purple",
] as const;

export type LabelColor = (typeof LABEL_COLORS)[number];

// ── "All projects" ─────────────────────────────────────────────────────────
// A SYNTHETIC project id: a route segment, never a row. Selecting it in the
// project switcher shows every work item in the workspace instead of one project's.
//
// Prefixed rather than a bare "all" so it can never collide with a real project
// id and so the check stays a cheap prefix test at the call site.
const ALL_PROJECTS_ID_PREFIX = "tp_all_";
export const ALL_PROJECTS_NAME = "All departments";

export function allProjectsId(workspaceId: string): string {
  return `${ALL_PROJECTS_ID_PREFIX}${workspaceId}`;
}

export function isAllProjectsId(projectId: string | null | undefined): boolean {
  return !!projectId && projectId.startsWith(ALL_PROJECTS_ID_PREFIX);
}

// Narrow the workspace's tasks to one project's work items: an exact projectId
// match. Generic so callers don't import the Task type.
export function tasksForProject<T extends { projectId?: string | null }>(
  tasks: readonly T[],
  projectId: string,
): T[] {
  // "All projects" is the whole workspace — the absence of a project filter, not a project.
  if (isAllProjectsId(projectId)) return [...tasks];
  return tasks.filter((t) => t.projectId === projectId);
}

// A person who can file into no project (server: db/task-link-scope.ts NO_FILEABLE_PROJECT).
export const NO_FILEABLE_PROJECT_TEXT =
  "You're not in any project yet. Ask a workspace admin to add you to a project's channel.";
const NO_FILEABLE_PROJECT_REFUSAL = "no project you can file into: pass projectId";

// What a failed task create says to the person: the server's own words, but the no-project
// refusal (it carries only the generic invalid_request code) in plain language.
export function createTaskErrorText(err: unknown): string {
  if (!(err instanceof Error)) return "Couldn't create the task.";
  return err.message === NO_FILEABLE_PROJECT_REFUSAL ? NO_FILEABLE_PROJECT_TEXT : err.message;
}
